/**
 * Business Process Detail Route
 *
 * Displays comprehensive business process metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useBusinessProcess } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteBusinessProcess, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './businessProcessId.css';

export const Route = createFileRoute('/business-processes/$businessProcessId')({
  component: BusinessProcessDetailPage,
});

function BusinessProcessDetailPage(): JSX.Element {
  const { businessProcessId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch business process data
  const { data, loading, error: fetchError } = useBusinessProcess(businessProcessId);
  const businessProcess = data?.businessProcess;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="business-process-detail-page">
        <DetailSkeleton cards={5} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="business-process-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load business process"
          description={fetchError.message}
          actionLabel="Back to Business Processes"
          onAction={() =>
            navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Not found state
  if (!businessProcess) {
    return (
      <div className="business-process-detail-page">
        <EmptyState
          icon="search_off"
          title="Business process not found"
          description={`No business process found with ID: ${businessProcessId}`}
          actionLabel="Back to Business Processes"
          onAction={() =>
            navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({
      to: `/business-processes/${businessProcess.entityId}/edit` as string,
    });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteBusinessProcess(businessProcessId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessProcesses'],
      });

      // Navigate back to business processes list
      navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="business-process-detail-page">
      {/* Header */}
      <div className="business-process-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() =>
              navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } })
            }
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Business Processes
          </ForgeButton>
          <h1 className="forge-typography--headline4">{businessProcess.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {businessProcess.description || 'No description provided'}
          </p>
        </div>
        <div className="business-process-detail-actions">
          <PermissionGate permission="business-process:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="business-process:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="business-process-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Business Process ID</span>
              <span className="detail-value">{businessProcess.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Process Type</span>
              <span className="detail-value">{businessProcess.processType || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Process Tier</span>
              <span className="detail-value">{businessProcess.processTier || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className="detail-value">{businessProcess.status || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Automation Level</span>
              <span className="detail-value">{businessProcess.automationLevel || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Process Details Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Process Details</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Process Owner ID</span>
              <span className="detail-value">{businessProcess.processOwnerId || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Owning Business Unit ID</span>
              <span className="detail-value">{businessProcess.owningBusinessUnitId || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Estimated Duration</span>
              <span className="detail-value">{businessProcess.estimatedDuration || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Volume Per Period</span>
              <span className="detail-value">{businessProcess.volumePerPeriod || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Descriptions Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Descriptions</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Input Description</span>
              <span className="detail-value">{businessProcess.inputDescription || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Output Description</span>
              <span className="detail-value">{businessProcess.outputDescription || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Tooling Description</span>
              <span className="detail-value">{businessProcess.toolingDescription || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {businessProcess.tags && businessProcess.tags.length > 0
                  ? businessProcess.tags.join(', ')
                  : '—'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{businessProcess.notes || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(businessProcess._createdAt)} by {businessProcess._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(businessProcess._updatedAt)} by {businessProcess._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Business Process"
          message="Are you sure you want to delete this business process?"
          warning="This action will soft delete the business process. It can be recovered by an administrator."
          itemName={businessProcess.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
